import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { passwordRequirements } from '~/models/passwordRequirements';
import { PasswordService } from '~/services/identity/password';
import { SecurityService } from '~/services/security.service';


@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.html',
    styleUrls: ['./reset-password.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'reset-password',
    },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  _password = new FormControl();
  _confirmPassword = new FormControl();
  _errorMessage: string;
  _resetRequiredMessage: string;
  _successMessage: string;
  _email: string;
  _token: string;
  _isComplex: string;
  resetRequired: string;
  _disabled: boolean;
  _passwordRequirements: passwordRequirements;
  _regLowerCase = new RegExp('[a-z]');
  _regUpperCase = new RegExp('[A-Z]');
  _regNumeric = new RegExp('[0-9]');
  _regSpecial = new RegExp('[!_.#$%&*@]');
  _regSpace = new RegExp('[ ]');

  constructor(private _securityService: SecurityService,
    private _passwordService: PasswordService,
    private route: ActivatedRoute,
    private router: Router) { }

  ngOnInit() {
    if (localStorage.getItem('accessToken')) {
      this._securityService.logout().subscribe();
    }

    this._securityService.removeLocalCredentials(true);

    this._email = this.route.snapshot.queryParams['email'] || '';
    this._token = this.route.snapshot.queryParams['token'] || '';
    this._isComplex = this.route.snapshot.queryParams['isComplex'] || '';
    this.resetRequired = this.route.snapshot.queryParams['resetRequired'] || '';

    if (this.resetRequired == "true") {
      this._resetRequiredMessage = "Reset password is required to login"
    }

    if (this._isComplex == 'True') {
      this._passwordRequirements = {
        includeLowercase: {
          text: 'Must have at least one lowercase character',
          valid: ''
        },
        includeUppercase: {
          text: 'Must have at least one uppercase character',
          valid: ''
        },
        includeNumeric: {
          text: 'Must have at least one numeric character',
          valid: ''
        },
        includeSpecial: {
          text: 'Must have at least one special character; e.g. !_.#$%&*@',
          valid: ''
        },
        notIncludeSpaces: {
          text: 'Must not include spaces',
          valid: ''
        },
        atLeastChar: {
          text: 'Must include at least 8 characters',
          valid: ''
        },
      };
    }
    else {
      this._passwordRequirements = {
        atLeastChar: {
          text: 'Must include at least 6 characters',
          valid: ''
        },
        notIncludeSpaces: { text: '', valid: '' },
        includeSpecial: { text: '', valid: '' },
        includeLowercase: { text: '', valid: '' },
        includeNumeric: { text: '', valid: '' },
        includeUppercase: { text: '', valid: '' },
      };
    }

    this._password.valueChanges.subscribe((x) => {
      if (this._isComplex == 'True') {
        this._passwordRequirements.includeLowercase.valid = this._regLowerCase.test(x) ? 'valid' : 'not-valid';
        this._passwordRequirements.includeUppercase.valid = this._regUpperCase.test(x) ? 'valid' : 'not-valid';
        this._passwordRequirements.includeNumeric.valid = this._regNumeric.test(x) ? 'valid' : 'not-valid';
        this._passwordRequirements.includeSpecial.valid = this._regSpecial.test(x) ? 'valid' : 'not-valid';
        this._passwordRequirements.notIncludeSpaces.valid = !this._regSpace.test(x) ? 'valid' : 'not-valid';
        this._passwordRequirements.atLeastChar.valid = x.length >= 8 ? 'valid' : 'not-valid';
      }
      else {
        this._passwordRequirements.atLeastChar.valid = x.length >= 6 ? 'valid' : 'not-valid';
      }
    });
  }

  _passwordValidated() {
    if (this._isComplex == 'True') {
      if (this._passwordRequirements.includeLowercase.valid != 'valid') {
        return false;
      }
      else if (this._passwordRequirements.includeUppercase.valid != 'valid') {
        return false;
      }
      else if (this._passwordRequirements.includeNumeric.valid != 'valid') {
        return false;
      }
      else if (this._passwordRequirements.includeSpecial.valid != 'valid') {
        return false;
      }
      else if (this._passwordRequirements.notIncludeSpaces.valid != 'valid') {
        return false;
      }
    }

    if (this._passwordRequirements.atLeastChar.valid != 'valid') {
      return false;
    }
    else
      return true;
  }

  onSubmit() {
    if (!this._passwordValidated()) {
      this._errorMessage = 'Please enter a password that meets all the requirements!';
    }
    else
      if (this._password.value !== this._confirmPassword.value) {
        this._errorMessage = 'Passwords should be compatible!';
      }
      else {
        const model = {
          email: this._email,
          token: this._token,
          password: this._password.value,
        };
        this._passwordService.reset(model).subscribe((result: any) => {
          if (result.success) {
            this._successMessage = 'Your password has been updated successfully!';
          }
          else {
            this._errorMessage = 'Please enter a password that meets all the requirements!';
          }
        },
          (error: any) => {
            this._errorMessage = error;
          });
      }
  }

  onReturn() {
    this.router.navigate(['/login']);
  }
}
